import {
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import {
	MSAL_INTERCEPTOR_CONFIG,
	MsalInterceptorConfiguration,
} from "@azure/msal-angular";
import { EMPTY, Observable, from, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { AzureSsoService } from "./azure-sso.service";

@Injectable()
export class AzureAuthInterceptor implements HttpInterceptor {
	constructor(
		@Inject(MSAL_INTERCEPTOR_CONFIG)
		private config: MsalInterceptorConfiguration,
		private azureSsoService: AzureSsoService
	) {}

	intercept(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		const scopes = this.getScopes(request.url);
		if (!scopes) {
			return next.handle(request);
		}

		if (this.azureSsoService.isSessionChangedInAnotherTab()) {
			return EMPTY;
		}

		return from(this.azureSsoService.acquireAccessToken(scopes)).pipe(
			switchMap((accessToken) => {
				if (!accessToken) {
					return EMPTY;
				}

				return next.handle(
					request.clone({
						setHeaders: {
							Authorization: `Bearer ${accessToken}`,
						},
					})
				);
			}),
			catchError((error) => throwError(error))
		);
	}

	private getScopes(url: string): string[] | null {
		for (const [resource, configuredScopes] of this.config
			.protectedResourceMap) {
			if (!this.matchesResource(url, resource)) {
				continue;
			}

			const scopes = configuredScopes?.filter(
				(scope): scope is string => typeof scope === "string"
			);
			return scopes?.length ? scopes : null;
		}

		return null;
	}

	private matchesResource(url: string, resource: string): boolean {
		const normalizedUrl = url.toLowerCase();
		const normalizedResource = resource.toLowerCase();

		if (normalizedResource.endsWith("/*")) {
			return normalizedUrl.startsWith(normalizedResource.slice(0, -1));
		}

		return normalizedUrl === normalizedResource;
	}
}
